import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiAward, FiUsers, FiTrendingUp, FiShield } = FiIcons;

function About() {
  const values = [
    {
      icon: FiAward,
      title: 'Quality Excellence',
      description: 'We maintain the highest standards in every project we undertake.'
    },
    {
      icon: FiUsers,
      title: 'Expert Team',
      description: 'Our skilled professionals bring decades of combined experience.'
    },
    {
      icon: FiTrendingUp,
      title: 'Innovation',
      description: 'We embrace cutting-edge technology and modern construction methods.'
    },
    {
      icon: FiShield,
      title: 'Safety First',
      description: 'Safety is our top priority in every aspect of our work.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>About BuildCorp | Leading Construction Company with 20+ Years Experience</title>
        <meta name="description" content="Learn about BuildCorp's 20+ years of construction excellence. Our expert team delivers quality residential and commercial building solutions with safety and innovation." />
        <meta name="keywords" content="about BuildCorp, construction company history, experienced builders, construction expertise, building professionals" />
      </Helmet>

      <div className="pt-20">
        {/* Hero Section */}
        <section className="bg-gray-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-secondary mb-6">
                About <span className="text-primary">BuildCorp</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Building dreams with precision, quality, and innovation for over two decades
              </p>
            </motion.div>
          </div>
        </section>

        {/* Story Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-3xl font-bold text-secondary mb-6">Our Story</h2>
                <p className="text-gray-600 mb-4">
                  Founded in 2003, BuildCorp has grown from a small local contractor to one of the region's most trusted construction companies. Our journey began with a simple mission: to deliver exceptional construction services that exceed client expectations.
                </p>
                <p className="text-gray-600 mb-4">
                  Over the years, we've completed hundreds of projects, from residential homes to large commercial complexes. Our commitment to quality, safety, and innovation has earned us a reputation for excellence in the construction industry.
                </p>
                <p className="text-gray-600">
                  Today, we continue to push the boundaries of what's possible in construction, embracing new technologies and sustainable building practices to create structures that stand the test of time.
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative"
              >
                <img
                  src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  alt="Construction team at work"
                  className="rounded-lg shadow-lg"
                />
                <div className="absolute -bottom-6 -right-6 bg-primary text-white p-6 rounded-lg">
                  <div className="text-center">
                    <div className="text-3xl font-bold">20+</div>
                    <div className="text-sm">Years Experience</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="bg-gray-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl font-bold text-secondary mb-4">Our Core Values</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                These fundamental principles guide everything we do and shape our commitment to excellence
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white p-6 rounded-lg shadow-lg text-center hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={value.icon} className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-secondary mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl font-bold text-secondary mb-4">Meet Our Leadership</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Our experienced leadership team brings together decades of construction expertise
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  name: 'John Smith',
                  position: 'CEO & Founder',
                  image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
                },
                {
                  name: 'Sarah Johnson',
                  position: 'Project Manager',
                  image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
                },
                {
                  name: 'Mike Davis',
                  position: 'Lead Engineer',
                  image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
                }
              ].map((member, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
                >
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-64 object-cover"
                  />
                  <div className="p-6 text-center">
                    <h3 className="text-xl font-semibold text-secondary mb-2">{member.name}</h3>
                    <p className="text-primary font-medium">{member.position}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default About;